/**
 * Per-section access guard for the admin dashboard.
 *
 * The `_admin` layout route already keeps non-staff out entirely; this narrows
 * a moderator to their granted sections so deep links can't render pages their
 * sidebar hides. UI-level only — every admin API re-checks the same grants
 * server-side (`requireAdminSection` middleware in the backend).
 *
 * Called from a route's `beforeLoad`, where the layout above has already put
 * the capabilities in the router context, so this needs no fetch of its own.
 */

import { redirect } from "@tanstack/react-router";
import type { ViewCapabilities } from "@/core/lib/view-mode";

import type { ModeratorSection } from "@/lib/moderator-sections";

/**
 * Allows admins always; moderators only when they hold the given section grant.
 * Call with no section for admin-only pages (Settings, AI, Subscriptions,
 * Usage, Audit, Secrets). Redirects everyone else to the admin Overview, which
 * all staff may see.
 */
export function requireAdminSection(
  capabilities: ViewCapabilities,
  section?: ModeratorSection,
): void {
  if (capabilities.isAdmin) return;
  if (section && capabilities.isModerator && capabilities.moderatorSections.includes(section)) {
    return;
  }
  throw redirect({ to: "/" });
}
